// 📅 ProgramSection - Section du programme détaillé de l'événement
// Affiche la timeline complète de la journée (déambulation, pré-concert, concert, after)
// Utilisé dans : src/pages/HomePage.jsx

import { Clock, MapPin, Music, Utensils, PartyPopper } from 'lucide-react'
import eventData from '@/locales/event.json'

/**
 * Composant de présentation du programme de la journée
 * Affiche une timeline verticale avec toutes les activités de l'événement
 * Le concert principal est mis en évidence visuellement
 */
export default function ProgramSection() {
  const { schedule, program } = eventData

  return (
    <section className="section-padding bg-white dark:bg-neutral-900">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display text-primary-600 dark:text-primary-400 mb-4">
            {program.title}
          </h2>
          <p className="text-lg text-neutral-700 dark:text-neutral-300 max-w-2xl mx-auto">
            {program.description}
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Déambulation */}
          <div className="relative pl-8 border-l-4 border-primary-500">
            <div className="absolute -left-3 top-0 w-6 h-6 bg-primary-500 rounded-full border-4 border-white dark:border-neutral-900" />
            <div className="bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                  <Music className="text-primary-600 dark:text-primary-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                    {program.deambulation.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                    {program.deambulation.description}
                  </p>
                  <div className="space-y-3">
                    {program.deambulation.schedule.map((horaire, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm">
                        <Clock className="text-primary-500" size={16} />
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">
                          {horaire.time}
                        </span>
                        <MapPin className="text-neutral-400" size={16} />
                        <span className="text-neutral-600 dark:text-neutral-400">
                          {horaire.location}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pré-concert */}
          <div className="relative pl-8 border-l-4 border-primary-500">
            <div className="absolute -left-3 top-0 w-6 h-6 bg-primary-500 rounded-full border-4 border-white dark:border-neutral-900" />
            <div className="bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                  <Utensils className="text-primary-600 dark:text-primary-400" size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="text-primary-500" size={20} />
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                      {schedule.preconcert.time} - Ambiance pré-concert
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {schedule.preconcert.activities.map((activite, index) => (
                      <li key={index} className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                        <span className="w-2 h-2 bg-primary-500 rounded-full" />
                        {activite}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Concert principal */}
          <div className="relative pl-8 border-l-4 border-primary-500">
            <div className="absolute -left-3 top-0 w-6 h-6 bg-primary-500 rounded-full border-4 border-white dark:border-neutral-900 animate-pulse" />
            <div className="bg-gradient-to-br from-primary-50 to-white dark:from-primary-950 dark:to-neutral-900 rounded-2xl p-6 shadow-xl border-2 border-primary-200 dark:border-primary-800">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-500 rounded-xl">
                  <Music className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="text-primary-600 dark:text-primary-400" size={20} />
                    <h3 className="text-2xl font-display font-bold text-primary-600 dark:text-primary-400">
                      {schedule.concert.time} - CONCERT PRINCIPAL
                    </h3>
                  </div>
                  <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-2">
                    {schedule.concert.description}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Fin prévue : {schedule.concert.end}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* After party */}
          <div className="relative pl-8">
            <div className="absolute -left-3 top-0 w-6 h-6 bg-primary-500 rounded-full border-4 border-white dark:border-neutral-900" />
            <div className="bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                  <PartyPopper className="text-primary-600 dark:text-primary-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                    After Party
                  </h3>
                  <p className="text-neutral-700 dark:text-neutral-300">
                    {schedule.afterParty.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            Une journée complète de festivités vous attend !
          </p>
        </div>
      </div>
    </section>
  )
}
